/**
 * SSEProgressCard — In-progress placeholder card for tables being profiled.
 *
 * UX Spec Section 5.2.3 / Story 1.3 AC#2.
 *
 * Three phases: Schema (0–30%), Statistics (30–70%), Analysis (70–100%).
 * Transitions to full TableCard on completion. Error state with retry.
 */

import { AlertCircle, Loader2 } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProfilingPhase = "schema" | "statistics" | "analysis";

export interface SSEProgressCardProps {
  tableName: string;
  /** 0–100 */
  progress: number;
  phase: ProfilingPhase;
  /** Error message if profiling failed */
  error?: string;
  /** Whether retry is available */
  retryable?: boolean;
  /** Compact variant for smaller viewports */
  compact?: boolean;
  onRetry?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PHASE_LABELS: Record<ProfilingPhase, string> = {
  schema: "Discovering schema…",
  statistics: "Computing statistics…",
  analysis: "Analyzing quality…",
};

function phaseFromProgress(progress: number): ProfilingPhase {
  if (progress < 30) return "schema";
  if (progress < 70) return "statistics";
  return "analysis";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SSEProgressCard({
  tableName,
  progress,
  phase,
  error,
  retryable = false,
  compact = false,
  onRetry,
}: SSEProgressCardProps) {
  const resolvedPhase = phase ?? phaseFromProgress(progress);
  const clampedProgress = Math.min(100, Math.max(0, progress));

  // Error state
  if (error) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`
          rounded-xl border-2 border-dashed border-[var(--destructive)]/40
          bg-[var(--destructive)]/5 p-4
          ${compact ? "min-h-[80px]" : "min-h-[140px]"}
          flex flex-col justify-center
        `}
      >
        <div className="flex items-center gap-2 text-[var(--destructive)]">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="text-body font-medium truncate">{tableName}</span>
        </div>
        <p className="text-caption text-[var(--destructive)]/80 mt-1 line-clamp-2">
          {error}
        </p>
        {retryable && onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 text-caption font-medium text-[var(--destructive)] hover:underline self-start"
          >
            Failed — click to retry
          </button>
        )}
      </div>
    );
  }

  // In-progress state
  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        rounded-xl border-2 border-dashed border-[var(--border)]
        glass-surface p-4
        ${compact ? "min-h-[80px]" : "min-h-[140px]"}
        flex flex-col justify-between
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <Loader2 className="w-4 h-4 text-[var(--primary)] animate-spin" />
        <span className="text-body font-medium text-[var(--foreground)] truncate">
          {tableName}
        </span>
      </div>

      {/* Phase label */}
      <p className="text-caption text-[var(--muted-foreground)] mt-2">
        {PHASE_LABELS[resolvedPhase]}
      </p>

      {/* Progress bar */}
      <div className="mt-auto pt-3">
        <div
          className="h-1.5 rounded-full bg-[var(--muted-foreground)]/20 overflow-hidden"
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${tableName} profiling: ${clampedProgress}%`}
        >
          <div
            className="h-full rounded-full bg-[var(--primary)] transition-all duration-500 ease-out"
            style={{ width: `${clampedProgress}%` }}
          />
        </div>
        {!compact && (
          <div className="flex justify-between mt-1">
            <span className="text-caption text-[var(--muted-foreground)]">
              {resolvedPhase.charAt(0).toUpperCase() + resolvedPhase.slice(1)}
            </span>
            <span className="text-caption text-[var(--muted-foreground)]">
              {clampedProgress}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
